import React from 'react';
import { Trees } from 'lucide-react';
import { useParams } from 'react-router-dom';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useFeatureFlag from 'src/shared/hooks/useFeatureFlag';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import Loader from 'src/shared/components/loader/Loader';
import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import RiskScoreCard from 'src/shared/components/risk-score-card/RiskScoreCard';
import SonFactorsPanel from 'src/shared/components/son-factors-panel/SonFactorsPanel';
import { useGetSiteStateOfNatureQuery } from 'src/app/site-nature-risk/state/api/siteNatureRiskEngineSlice';
import type { ScoreBand } from 'src/shared/components/risk-score-cell/riskScoreColour';
import ReportingFrameworks from 'src/shared/components/reporting-frameworks/ReportingFrameworks';

const FEATURE_ID = 'risk-insights';

const BAND_MAP: Record<'VERY_POOR' | 'POOR' | 'MODERATE' | 'GOOD' | 'VERY_GOOD', ScoreBand> = {
    VERY_POOR: 1,
    POOR: 2,
    MODERATE: 3,
    GOOD: 4,
    VERY_GOOD: 5,
};

const bandFromGqlEnum = (gqlBand: 'VERY_POOR' | 'POOR' | 'MODERATE' | 'GOOD' | 'VERY_GOOD'): ScoreBand => BAND_MAP[gqlBand];

const SiteNatureRiskStateOfNature = () => {
    const { siteId } = useParams<{ siteId: string }>();
    const { currentCustomerId } = useCustomer();
    const navigation = useAppNavigation();
    const { enabled, loading } = useFeatureFlag({ featureId: FEATURE_ID });

    const { data, isLoading } = useGetSiteStateOfNatureQuery(
        { organisationBusinessCentralId: currentCustomerId, siteId: siteId! },
        { skip: !currentCustomerId || !siteId }
    );

    if (loading) return <Loader />;
    if (!enabled) return <PageNotFound />;

    const site = data?.site;
    const categories = site?.sonCategoryBreakdown ?? [];

    const breadcrumbs = [{ label: 'Nature Risk', path: navigation.globalNatureRiskOverviewPath }, { label: site?.name ?? '' }];

    return (
        <StandardAppLayout
            title={site?.name ?? ''}
            subTitle='State of Nature'
            breadcrumbs={breadcrumbs}
            stickyHeader={false}
            mainContent={
                <div className='flex flex-col w-full gap-6 flex-1 min-h-0 pt-1 mb-6' data-testid='site-nature-risk-state-of-nature-main'>
                    <div className='flex flex-row gap-4'>
                        <div className='flex-[2] flex flex-col'>
                            <RiskScoreCard
                                title='Overall State of Nature'
                                value={site?.riskSummary?.stateOfNature ?? null}
                                scoreType='Condition'
                                isLoading={isLoading}
                                size='lg'
                                slug='state-of-nature'
                                icon={Trees}
                                className='h-full'
                            />
                        </div>
                        {categories.map(category => (
                            <div key={category.sourceKey} className='flex-1 flex flex-col'>
                                <RiskScoreCard
                                    title={category.name}
                                    value={category.score}
                                    scoreType='Condition'
                                    fullBadgeLabel={false}
                                    isLoading={isLoading}
                                    size='md'
                                    slug={`son_${category.sourceKey}`}
                                    showBarometer={false}
                                    className='h-full'
                                />
                            </div>
                        ))}
                    </div>

                    {categories.some(c => (c.factors?.length ?? 0) > 0) && (
                        <div className='flex flex-col gap-3'>
                            <div className='flex items-center justify-between'>
                                <span className='text-[20px] font-normal text-[#212121]'>State of Nature category breakdown</span>
                                <ReportingFrameworks slugPrefix='gnr-son' />
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                {categories.map(category => (
                                    <SonFactorsPanel
                                        key={`${category.sourceKey}-factors`}
                                        title={category.name}
                                        slug={category.sourceKey}
                                        factors={(category.factors ?? [])
                                            .map(f => ({
                                                name: f.name,
                                                sourceKey: f.sourceKey,
                                                weight: f.weight,
                                                band: bandFromGqlEnum(f.band),
                                            }))
                                            .sort((a, b) => b.band - a.band)}
                                        isLoading={isLoading}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default SiteNatureRiskStateOfNature;
